import React, { useState, useEffect, useCallback } from 'react';
import PageHeader from '../../components/PageHeader';
import { Mail, Download, Calendar, Users, TrendingUp, AlertCircle, CheckCircle2, Send, RefreshCw } from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

function todayISO() {
  // IST today (UTC+5:30)
  const now = new Date();
  const ist = new Date(now.getTime() + (5.5 * 60 + now.getTimezoneOffset()) * 60_000);
  return ist.toISOString().slice(0, 10);
}

function KPI({ label, value, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5">
      <div className="kpi-overline">{label}</div>
      <div className={`text-3xl font-display font-bold mt-2 ${accent || 'text-slate-800'}`}>{value}</div>
    </div>
  );
}

export default function DailyAttendanceReport() {
  const { user } = useAuth() || {};
  const role = (user?.role || '').toLowerCase();
  const dept = (user?.department || '').toUpperCase();
  const isSuper = role === 'super_admin';
  const canSendToAll = isSuper;
  const canView = isSuper || ['md', 'ceo', 'cmd', 'cfo', 'hr'].includes(role) || ['MD', 'CEO', 'CMD', 'CFO', 'HR'].includes(dept);

  const [date, setDate] = useState(todayISO());
  const [companyFilter, setCompanyFilter] = useState(''); // '' = active company, 'all' = combined, or specific id
  const [companies, setCompanies] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [recipients, setRecipients] = useState('');
  const [testMode, setTestMode] = useState(true);
  const canCombined = isSuper || ['md', 'ceo', 'cmd', 'cfo'].includes(role);

  useEffect(() => {
    (async () => {
      try {
        const c = await apiGet('/companies');
        setCompanies(c || []);
      } catch { /* silent */ }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ date });
      if (companyFilter) qs.set('company_id', companyFilter);
      const d = await apiGet(`/reports/attendance/daily?${qs}`);
      setData(d);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load attendance');
      setData(null);
    }
    setLoading(false);
  }, [date, companyFilter]);

  useEffect(() => { if (canView) load(); }, [canView, load]);

  const sendEmail = async () => {
    setSending(true);
    try {
      const payload = { date, test_mode: testMode };
      if (companyFilter) payload.company_id = companyFilter;
      if (!testMode && recipients.trim()) {
        payload.to_emails = recipients.split(/[, \n;]+/).map(s => s.trim()).filter(Boolean);
      }
      const r = await apiPost('/reports/attendance/daily/send', payload);
      toast.success(`Sent to ${r.recipient_count} recipient${r.recipient_count === 1 ? '' : 's'}: ${r.sent_to.join(', ')}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Send failed');
    }
    setSending(false);
  };

  // Fire the exact APScheduler job that auto-runs at 10:00 IST — sends the full
  // per-company leadership digest immediately (super admin only).
  const triggerNow = async () => {
    setTriggering(true);
    try {
      const r = await apiPost('/notifications/trigger/daily-attendance-digest', {});
      toast.success(r?.message || 'Daily attendance digest dispatched to leadership.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not dispatch digest');
    }
    setTriggering(false);
  };

  const downloadExcel = async () => {
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const tok = localStorage.getItem('access_token');
      const qs = new URLSearchParams({ date });
      if (companyFilter) qs.set('company_id', companyFilter);
      const res = await fetch(`${API}/api/reports/attendance/daily.xlsx?${qs}`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `Daily_Attendance_${date}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Excel download failed');
    }
  };

  const downloadLabourExcel = async () => {
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const tok = localStorage.getItem('access_token');
      const qs = new URLSearchParams({ date });
      const res = await fetch(`${API}/api/reports/attendance/labour.xlsx?${qs}`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `Labour_Attendance_${date}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Labour Excel download failed');
    }
  };

  if (!canView) {
    return (
      <div>
        <PageHeader overline="Reports & MIS" title="Daily Attendance Report" />
        <div className="bg-rose-50 border border-rose-200 rounded-sm p-8 text-center">
          <AlertCircle className="mx-auto text-rose-500 mb-3" size={32} />
          <p className="font-display font-bold text-rose-700">Access restricted</p>
          <p className="text-sm text-rose-600 mt-1">Only Super Admin, MD, CEO, CMD, CFO and HR can view this report.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="daily-attendance-report">
      <PageHeader
        overline="Reports & MIS · Live"
        title="Daily Attendance Report"
        subtitle="Project-wise headcount snapshot. Auto-sent at 10:00 IST to MD / CEO / CMD / CFO / HR / Super Admin."
      />

      {/* Control bar */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 mb-5 flex flex-wrap items-end gap-3">
        <div>
          <label className="kpi-overline mb-1 block">Report Date</label>
          <div className="relative">
            <Calendar size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="pl-8 pr-3 py-2 border border-slate-300 rounded-sm text-sm font-mono"
              data-testid="dar-date-input"
            />
          </div>
        </div>
        {companies.length > 1 && (
          <div>
            <label className="kpi-overline mb-1 block">Company</label>
            <select
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white"
              data-testid="dar-company-filter"
            >
              <option value="">Active Company</option>
              {companies.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
              {canCombined && <option value="all">All Companies (Combined)</option>}
            </select>
          </div>
        )}
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs uppercase tracking-wider font-display font-bold rounded-sm disabled:opacity-50"
          data-testid="dar-refresh-btn"
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
        <button
          onClick={downloadExcel}
          className="inline-flex items-center gap-2 px-4 py-2 border border-[#1F7A6E] bg-white hover:bg-teal-50 text-[#1F7A6E] text-xs uppercase tracking-wider font-display font-bold rounded-sm"
          data-testid="dar-download-xlsx"
        >
          <Download size={13} /> Staff Excel
        </button>
        <button
          onClick={downloadLabourExcel}
          className="inline-flex items-center gap-2 px-4 py-2 border border-[#0f766e] bg-white hover:bg-teal-50 text-[#0f766e] text-xs uppercase tracking-wider font-display font-bold rounded-sm"
          data-testid="dar-download-labour-xlsx"
        >
          <Download size={13} /> Labour Excel
        </button>
        <div className="flex-1" />
        <div className="text-[11px] text-slate-500">
          {data?.date ? `Snapshot: ${data.date}` : '—'}
        </div>
      </div>

      {/* KPIs */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-5" data-testid="dar-kpis">
          <KPI label={`Projects · Offices`} value={`${data.project_count || 0} · ${data.office_count || 0}`} />
          <KPI label="Total Employees" value={data.totals?.total || 0} accent="text-slate-800" />
          <KPI label="Employees Present" value={data.totals?.P || 0} accent="text-emerald-600" />
          <KPI label="Labour Present" value={data.totals?.labour_present || 0} accent="text-teal-700" />
          <KPI label="Employee Att %" value={`${data.attendance_pct || 0}%`} accent="text-[#1F7A6E]" />
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden mb-5">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
          <Users size={14} className="text-slate-500" />
          <h3 className="font-display font-bold text-sm">Project-wise Breakdown</h3>
          <span className="ml-auto text-[10px] text-slate-500">Employees from <code>hr_attendance</code> · Labour from <code>labour_attendance</code> + <code>mobile_labour</code></span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="dar-table">
            <thead>
              <tr className="bg-[#1c2a4d] text-white">
                <th rowSpan={2} className="px-4 py-2.5 text-left text-[11px] uppercase tracking-wider font-display border-r border-[#14504E]">Project / Office</th>
                <th colSpan={6} className="px-3 py-1.5 text-center text-[11px] uppercase tracking-wider font-display bg-[#243561] border-r border-[#0f766e]">Employees (Roll)</th>
                <th colSpan={3} className="px-3 py-1.5 text-center text-[11px] uppercase tracking-wider font-display bg-teal-700">Labour (Site)</th>
                <th rowSpan={2} className="px-3 py-2.5 text-right text-[11px] uppercase tracking-wider font-display border-l border-[#14504E]">Total<br/>Workforce</th>
              </tr>
              <tr className="bg-[#1c2a4d] text-white">
                <th className="px-3 py-1.5 text-right text-[10px] font-display">HC</th>
                <th className="px-3 py-1.5 text-right text-[10px] font-display text-emerald-300">Present</th>
                <th className="px-3 py-1.5 text-right text-[10px] font-display text-rose-300">Absent</th>
                <th className="px-3 py-1.5 text-right text-[10px] font-display">Leave</th>
                <th className="px-3 py-1.5 text-right text-[10px] font-display text-amber-300">Not&nbsp;Marked</th>
                <th className="px-3 py-1.5 text-right text-[10px] font-display border-r border-[#0f766e]">Att %</th>
                <th className="px-3 py-1.5 text-right text-[10px] font-display bg-teal-700">Sub</th>
                <th className="px-3 py-1.5 text-right text-[10px] font-display bg-teal-700">Mobile</th>
                <th className="px-3 py-1.5 text-right text-[10px] font-display bg-teal-700">Total</th>
              </tr>
            </thead>
            <tbody>
              {(data?.rows || []).map((r, i) => {
                const pct = r.total > 0 ? Math.round(100 * r.P / r.total * 10) / 10 : 0;
                const leaveTotal = (r.L || 0) + (r.LV || 0);
                const grand = (r.P || 0) + (r.labour_present || 0);
                const isOffice = !!r.is_office;
                const isUnassigned = r.project_id === '_unassigned';
                const label = isOffice
                  ? <span className="inline-flex items-center gap-1.5"><span className="px-1.5 py-0.5 text-[9px] uppercase font-bold bg-amber-100 text-amber-800 rounded-sm">Office</span>{r.project_name}</span>
                  : isUnassigned
                    ? <span className="inline-flex items-center gap-1.5"><span className="px-1.5 py-0.5 text-[9px] uppercase font-bold bg-teal-100 text-teal-800 rounded-sm">HQ</span>Head Office / HQ</span>
                    : r.project_name;
                return (
                  <tr key={i} className={`border-b border-slate-100 hover:bg-teal-50/30 ${isOffice ? 'bg-amber-50/40 italic text-slate-600' : ''}`} data-testid={`dar-row-${i}`}>
                    <td className="px-4 py-2.5 text-slate-800 font-medium">{label}</td>
                    <td className="px-3 py-2.5 text-right">{isOffice ? '—' : r.total}</td>
                    <td className="px-3 py-2.5 text-right text-emerald-700 font-bold">{r.P}</td>
                    <td className="px-3 py-2.5 text-right text-rose-700 font-bold">{isOffice ? '—' : r.A}</td>
                    <td className="px-3 py-2.5 text-right text-slate-600">{isOffice ? '—' : leaveTotal}</td>
                    <td className="px-3 py-2.5 text-right text-amber-700">{isOffice ? '—' : (r.NM || 0)}</td>
                    <td className="px-3 py-2.5 text-right font-bold border-r border-slate-200">{isOffice ? '—' : `${pct}%`}</td>
                    <td className="px-3 py-2.5 text-right text-teal-700">{isOffice ? '—' : (r.labour_subcontract || 0)}</td>
                    <td className="px-3 py-2.5 text-right text-teal-700">{isOffice ? '—' : (r.labour_mobile || 0)}</td>
                    <td className="px-3 py-2.5 text-right text-teal-700 font-bold">{isOffice ? '—' : (r.labour_present || 0)}</td>
                    <td className="px-3 py-2.5 text-right font-bold bg-slate-50">{grand}</td>
                  </tr>
                );
              })}
              {data && (data.rows || []).length === 0 && (
                <tr><td colSpan={11} className="px-4 py-8 text-center text-slate-400 text-sm">No attendance records for {date}.</td></tr>
              )}
              {data && (data.rows || []).length > 0 && (
                <tr className="bg-slate-100 border-t-2 border-[#1c2a4d] font-bold">
                  <td className="px-4 py-3">TOTAL (Projects + HQ)</td>
                  <td className="px-3 py-3 text-right">{data.totals.total}</td>
                  <td className="px-3 py-3 text-right text-emerald-700">{data.totals.P}</td>
                  <td className="px-3 py-3 text-right text-rose-700">{data.totals.A}</td>
                  <td className="px-3 py-3 text-right">{(data.totals.L || 0) + (data.totals.LV || 0)}</td>
                  <td className="px-3 py-3 text-right text-amber-700">{data.totals.NM || 0}</td>
                  <td className="px-3 py-3 text-right border-r border-slate-300">{data.attendance_pct}%</td>
                  <td className="px-3 py-3 text-right text-teal-700">{data.totals.labour_subcontract || 0}</td>
                  <td className="px-3 py-3 text-right text-teal-700">{data.totals.labour_mobile || 0}</td>
                  <td className="px-3 py-3 text-right text-teal-700">{data.totals.labour_present || 0}</td>
                  <td className="px-3 py-3 text-right bg-slate-200">{data.grand_total_present || 0}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Email send panel */}
      <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="dar-send-panel">
        <div className="flex items-center gap-2 mb-3">
          <Mail size={15} className="text-[#1F7A6E]" />
          <h3 className="font-display font-bold text-sm">Send by Email</h3>
        </div>

        {/* GLOBAL TEST-REDIRECT WARNING removed — test-mode mechanism deprecated (we are live). */}

        <p className="text-xs text-slate-600 mb-4">
          This report is auto-sent every day at <b>10:00 IST</b> to Super Admin, MD, CEO, CMD, CFO and HR.
          Use the controls below to manually send a copy now.
        </p>

        <div className="flex items-center gap-2 mb-3">
          <input
            id="dar-test-mode"
            type="checkbox"
            checked={testMode}
            onChange={(e) => setTestMode(e.target.checked)}
            className="accent-[#1F7A6E]"
            data-testid="dar-test-mode-toggle"
          />
          <label htmlFor="dar-test-mode" className="text-xs text-slate-700">
            <b>Test mode</b> — send only to me ({user?.email || '—'})
          </label>
        </div>

        {!testMode && canSendToAll && (
          <div className="mb-3">
            <label className="kpi-overline mb-1 block">Recipients (leave blank for default leadership list)</label>
            <textarea
              value={recipients}
              onChange={(e) => setRecipients(e.target.value)}
              placeholder="omkar@elloraepc.com, md@elloraepc.com, ..."
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono"
              rows={2}
              data-testid="dar-recipients-input"
            />
            <p className="text-[10px] text-slate-500 mt-1">Comma, space or newline separated. Blank → MD / CEO / CMD / CFO / HR / Super Admin.</p>
          </div>
        )}

        {!testMode && !canSendToAll && (
          <div className="mb-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2 flex items-start gap-2">
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <span>Only Super Admin can send to the full leadership list. Use Test mode to send to yourself.</span>
          </div>
        )}

        <button
          onClick={sendEmail}
          disabled={sending || !data || (data?.rows || []).length === 0}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1F7A6E] hover:bg-[#14504E] text-white text-xs uppercase tracking-wider font-display font-bold rounded-sm disabled:opacity-50"
          data-testid="dar-send-btn"
        >
          {sending ? <RefreshCw size={13} className="animate-spin" /> : <Send size={13} />}
          {sending ? 'Sending…' : testMode ? 'Send to me' : 'Send Email'}
        </button>

        {isSuper && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="text-[11px] text-slate-600 mb-2">
              Or push the full per-company leadership digest right now — exactly what the 10:00 IST job sends to MD / CEO / CMD / CFO / HR / Super Admin.
            </div>
            <button
              onClick={triggerNow}
              disabled={triggering}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs uppercase tracking-wider font-display font-bold rounded-sm disabled:opacity-50"
              data-testid="dar-trigger-now-btn"
            >
              {triggering ? <RefreshCw size={13} className="animate-spin" /> : <Mail size={13} />}
              {triggering ? 'Dispatching…' : 'Send Now to Leadership'}
            </button>
          </div>
        )}

        <div className="mt-4 flex items-start gap-2 text-[11px] text-slate-500">
          <CheckCircle2 size={12} className="mt-0.5 text-emerald-500" />
          <span>Cron job <code>daily_attendance_digest</code> runs at <b>04:30 UTC / 10:00 IST</b> every day automatically.</span>
        </div>
      </div>
    </div>
  );
}
